import { useCallback, useRef, useState, useEffect } from "react";

const STORAGE_KEY = "pdui:advancedMode";

/**
 * 5-tap easter egg to unlock the hidden admin diagnostics panel.
 *
 * Session-scoped: closing the tab re-hides the view. This matches the
 * local-UI implementation so operators see consistent behavior across
 * both control surfaces.
 */
export default function useSecretTap(tapsRequired = 5, windowMs = 2000) {
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return window.sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const tapsRef = useRef([]);

  const register = useCallback(() => {
    const now = Date.now();
    tapsRef.current = tapsRef.current.filter((t) => now - t < windowMs);
    tapsRef.current.push(now);
    if (tapsRef.current.length >= tapsRequired) {
      tapsRef.current = [];
      setUnlocked((prev) => {
        const next = !prev;
        try {
          if (next) window.sessionStorage.setItem(STORAGE_KEY, "1");
          else window.sessionStorage.removeItem(STORAGE_KEY);
        } catch {
          /* ignore */
        }
        return next;
      });
    }
  }, [tapsRequired, windowMs]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      tapsRef.current = tapsRef.current.filter((t) => now - t < windowMs);
    }, windowMs);
    return () => clearInterval(interval);
  }, [windowMs]);

  const lock = useCallback(() => {
    setUnlocked(false);
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  return { unlocked, register, lock };
}
